import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { setAuthTokens, setUnauthorizedHandler } from '../services/api';
import {
  authService,
  toPrincipal,
  type GoogleSignupRequired,
  type LoginRequest,
  type RegisterRequest,
  type RequestAccessRequest,
} from '../services/auth.service';
import { invitationsService, type AcceptedInvitation } from '../services/invitations.service';
import { config } from '../config/env';
import type { AuthTokens, UserMembership, UserPrincipal } from '../types/data-models';

interface PersistedAuth {
  user: UserPrincipal;
  tokens: AuthTokens;
  memberships: UserMembership[];
  /** From the token's `adm` claim. Cosmetic — the server re-checks every call. */
  isPlatformAdmin?: boolean;
}

/**
 * Reads the `adm` claim out of the access token. Purely so the admin nav can be
 * hidden; `/tenants` and `/users` are enforced server-side against
 * PLATFORM_ADMIN_EMAILS regardless of what this returns.
 */
function readPlatformAdminClaim(accessToken: string | undefined): boolean {
  if (!accessToken) return false;
  try {
    const [, payload] = accessToken.split('.');
    if (!payload) return false;
    const json = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    return json?.adm === true;
  } catch {
    return false;
  }
}

interface AuthContextValue {
  user: UserPrincipal | null;
  memberships: UserMembership[];
  isAuthenticated: boolean;
  /** True when the signed-in email is in PLATFORM_ADMIN_EMAILS. UI hint only. */
  isPlatformAdmin: boolean;
  loading: boolean;
  login: (payload: LoginRequest) => Promise<void>;
  register: (payload: RegisterRequest) => Promise<void>;
  /** Submit a self-serve "ask for access" request. Doesn't establish a session. */
  requestAccess: (payload: RequestAccessRequest) => Promise<void>;
  /**
   * Signs in with a Google ID token. Resolves to `null` once authenticated,
   * or to the signup handoff when the Google account has no account yet —
   * the caller then routes to the workspace-name step.
   */
  loginWithGoogle: (idToken: string) => Promise<GoogleSignupRequired | null>;
  completeGoogleSignup: (signupToken: string, tenantName: string) => Promise<void>;
  logout: () => void;
  /** Re-authenticate against a different tenant without re-entering a password. */
  switchTenant: (tenantId: number) => Promise<void>;
  /**
   * Accept a project invitation. `idToken` is a Google credential for a
   * signed-out invitee; omit it to accept as the currently signed-in user.
   * Establishes a session scoped to the invitee's OWN workspace.
   */
  acceptInvitation: (token: string, idToken?: string) => Promise<AcceptedInvitation>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function readPersisted(): PersistedAuth | null {
  try {
    const raw = localStorage.getItem(config.authStorageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PersistedAuth>;
    // Drop entries that don't match the current shape (e.g. left over from
    // an older login flow). Otherwise downstream `.tokens.accessToken` reads
    // crash on first render.
    if (!parsed?.user || !parsed?.tokens?.accessToken) {
      localStorage.removeItem(config.authStorageKey);
      return null;
    }
    return parsed as PersistedAuth;
  } catch {
    localStorage.removeItem(config.authStorageKey);
    return null;
  }
}

function writePersisted(value: PersistedAuth | null) {
  if (value) localStorage.setItem(config.authStorageKey, JSON.stringify(value));
  else localStorage.removeItem(config.authStorageKey);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const initial = readPersisted();
  const [user, setUser] = useState<UserPrincipal | null>(initial?.user ?? null);
  const [memberships, setMemberships] = useState<UserMembership[]>(initial?.memberships ?? []);
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(initial?.isPlatformAdmin ?? false);
  const [loading, setLoading] = useState(false);
  const queryClient = useQueryClient();

  // Sync the tokens into the axios interceptor on every change.
  useEffect(() => {
    setAuthTokens(initial?.tokens?.accessToken ?? null, initial?.tokens?.refreshToken ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const persist = useCallback((next: PersistedAuth | null) => {
    const withClaim = next
      ? { ...next, isPlatformAdmin: readPlatformAdminClaim(next.tokens?.accessToken) }
      : null;
    writePersisted(withClaim);
    setAuthTokens(withClaim?.tokens?.accessToken ?? null, withClaim?.tokens?.refreshToken ?? null);
    setUser(withClaim?.user ?? null);
    setMemberships(withClaim?.memberships ?? []);
    setIsPlatformAdmin(withClaim?.isPlatformAdmin ?? false);
  }, []);

  const login = useCallback(
    async (payload: LoginRequest) => {
      setLoading(true);
      try {
        const res = await authService.login(payload);
        persist({ user: res.user, tokens: res.tokens, memberships: res.memberships ?? [] });
      } finally {
        setLoading(false);
      }
    },
    [persist],
  );

  const register = useCallback(
    async (payload: RegisterRequest) => {
      setLoading(true);
      try {
        const res = await authService.register(payload);
        persist({ user: res.user, tokens: res.tokens, memberships: res.memberships ?? [] });
      } finally {
        setLoading(false);
      }
    },
    [persist],
  );

  const requestAccess = useCallback(async (payload: RequestAccessRequest) => {
    setLoading(true);
    try {
      await authService.requestAccess(payload);
    } finally {
      setLoading(false);
    }
  }, []);

  const loginWithGoogle = useCallback(
    async (idToken: string) => {
      setLoading(true);
      try {
        const res = await authService.loginWithGoogle(idToken);
        if (res.status === 'signup_required') return res;
        persist({ user: res.user, tokens: res.tokens, memberships: res.memberships });
        return null;
      } finally {
        setLoading(false);
      }
    },
    [persist],
  );

  const completeGoogleSignup = useCallback(
    async (signupToken: string, tenantName: string) => {
      setLoading(true);
      try {
        const res = await authService.completeGoogleSignup(signupToken, tenantName);
        persist({ user: res.user, tokens: res.tokens, memberships: res.memberships });
      } finally {
        setLoading(false);
      }
    },
    [persist],
  );

  const logout = useCallback(() => persist(null), [persist]);

  const switchTenant = useCallback(
    async (tenantId: number) => {
      // Server validates membership and issues a fresh tenant-scoped JWT.
      // After the swap, blow away the TanStack-Query cache so every list /
      // dropdown re-fetches under the new tenant scope — otherwise the UI
      // would briefly show stale data from the previous tenant.
      const current = readPersisted();
      if (!current) throw new Error('Not authenticated');
      const res = await authService.switchTenant(tenantId);
      // The response now carries memberships, so the previous ones are only a
      // fallback for an older backend.
      persist({
        user: res.user,
        tokens: res.tokens,
        memberships: res.memberships.length ? res.memberships : current.memberships,
      });
      queryClient.removeQueries();
    },
    [persist, queryClient],
  );

  const acceptInvitation = useCallback(
    async (token: string, idToken?: string) => {
      setLoading(true);
      try {
        const res = await invitationsService.accept(token, idToken);
        persist({
          user: toPrincipal(res.user),
          tokens: res.tokens,
          memberships: res.memberships,
        });
        // The invitee's project list has just changed; drop cached queries so
        // the shared project shows up immediately.
        queryClient.removeQueries();
        return res;
      } finally {
        setLoading(false);
      }
    },
    [persist, queryClient],
  );

  useEffect(() => {
    setUnauthorizedHandler(() => logout());
    return () => setUnauthorizedHandler(null);
  }, [logout]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      memberships,
      isAuthenticated: !!user,
      isPlatformAdmin,
      loading,
      login,
      register,
      requestAccess,
      loginWithGoogle,
      completeGoogleSignup,
      logout,
      switchTenant,
      acceptInvitation,
    }),
    [
      user,
      memberships,
      isPlatformAdmin,
      loading,
      login,
      register,
      requestAccess,
      loginWithGoogle,
      completeGoogleSignup,
      logout,
      switchTenant,
      acceptInvitation,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
